import JSZip from 'jszip'

export interface ExportMediaItem {
  /** Remote (S3/CloudFront) URL to fetch the asset from. */
  url: string
  /** Desired filename inside the archive (collisions are de-duplicated). */
  filename: string
}

export interface ExportCaptionItem {
  /** Platform key, used for the caption filename (e.g. `caption-instagram.txt`). */
  platform: string
  /** Optional title (e.g. YouTube), written above the caption body. */
  title?: string
  /** Caption text. */
  text: string
}

interface BuildExportOptions {
  media: ExportMediaItem[]
  captions: ExportCaptionItem[]
  /** Filename for the downloaded archive, including the `.zip` extension. */
  zipName: string
}

/** Strip characters that are unsafe or awkward in cross-platform filenames. */
function sanitizeFilename(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^[_.]+|_+$/g, '')
  return cleaned || 'file'
}

/** Suffix duplicate names (`a.png` → `a-2.png`) so no archive entry is overwritten. */
function dedupeName(used: Set<string>, name: string): string {
  if (!used.has(name)) {
    used.add(name)
    return name
  }
  const dot = name.lastIndexOf('.')
  const base = dot > 0 ? name.slice(0, dot) : name
  const ext = dot > 0 ? name.slice(dot) : ''
  let i = 2
  let candidate = `${base}-${i}${ext}`
  while (used.has(candidate)) {
    i += 1
    candidate = `${base}-${i}${ext}`
  }
  used.add(candidate)
  return candidate
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Fetches the selected media + captions entirely in the browser and downloads
 * them as a single zip. Media is fetched directly from S3/CloudFront (the
 * bucket CORS allows cross-origin GET), so no backend round-trip is needed.
 */
export async function buildAndDownloadExport({
  media,
  captions,
  zipName,
}: BuildExportOptions): Promise<void> {
  const zip = new JSZip()
  const used = new Set<string>()

  for (const item of media) {
    const response = await fetch(item.url)
    if (!response.ok) {
      throw new Error(`Failed to fetch media (${response.status}): ${item.filename}`)
    }
    const blob = await response.blob()
    zip.file(dedupeName(used, sanitizeFilename(item.filename)), blob)
  }

  for (const caption of captions) {
    const body = caption.title ? `${caption.title}\n\n${caption.text}` : caption.text
    zip.file(dedupeName(used, `caption-${caption.platform}.txt`), body)
  }

  const archive = await zip.generateAsync({ type: 'blob' })
  triggerBlobDownload(archive, zipName)
}
